import { REQUIRED_WEBEX_SCOPES } from "@p0security/shared/integrations/notifiers/webex/constants";
import { WebexConfig } from "@p0security/shared/integrations/notifiers/webex/types";
import { IntegrationConfig } from "@p0security/shared/integrations/shared";
import { Alert, Button, Input, Select, Typography } from "antd";
import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useFirestoreDoc } from "../../../providers/FirestoreProvider";
import { Tenant } from "../../Login/util";
import { OAuth2IntegrationCard } from "../IntegrationCard";
import { WebexLogo } from "./WebexLogo";

type WebexRoom = {
  id: string;
  title: string;
  type: string;
};

const Preamble: React.FC = () => {
  return (
    <>
      <Typography.Text>
        Complete these prerequisite steps to configure Webex notifications.
      </Typography.Text>
      <br />
      <br />
      <ul style={{ paddingLeft: "15px" }}>
        <li>
          Ensure the P0 Security bot has been added to your Webex organization
        </li>
        <li>
          Add the bot to the room where you want to receive notifications (you
          can do this before or after OAuth)
        </li>
      </ul>
      <Typography.Text>
        After completing the above steps, click the button below to continue
        with the installation:
      </Typography.Text>
      <br />
      <br />
    </>
  );
};

export const Webex: React.FC = () => {
  const tenantId = useContext(Tenant);
  const path = `o/${tenantId}/integrations/webex`;
  const config = useFirestoreDoc<IntegrationConfig & WebexConfig>(path, {
    live: true,
  });

  const [complete, setComplete] = useState(false);
  const [roomId, setRoomId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [rooms, setRooms] = useState<WebexRoom[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [checkingMembership, setCheckingMembership] = useState(false);
  const [membershipChecked, setMembershipChecked] = useState(false);
  const [isBotMember, setIsBotMember] = useState(false);
  const [addingBot, setAddingBot] = useState(false);

  const fetch = useAuthFetch(setError, setFetching);

  const selectedRoom = useMemo(
    () => rooms.find((r) => r.id === roomId),
    [rooms, roomId]
  );

  const isInstalled = config.doc?.data?.state === "installed";
  const loading = config.loading || fetching;

  const authExtra = useMemo(
    () =>
      `&response_type=code&scope=${encodeURIComponent(REQUIRED_WEBEX_SCOPES.join(" "))}`,
    []
  );

  useGuardedEffect(
    (cancellation) => async () => {
      const data = config.doc?.data;
      if (cancellation.isCancelled) return;
      if (data && data.roomId && isInstalled) {
        setRoomId(data.roomId);
        setComplete(true);
      }
    },
    [config.doc?.data, isInstalled],
    setError
  );

  // Fetch rooms when integration is installed
  useEffect(() => {
    if (!isInstalled || complete) return;

    const fetchRooms = async () => {
      setLoadingRooms(true);
      try {
        const response = await fetch("integrations/webex/rooms", {
          method: "GET",
        });
        if (response?.ok) {
          const data = await response.json();
          setRooms(data.rooms || []);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
  }, [isInstalled, complete, fetch]);

  const handleSubmit = useCallback(async () => {
    if (!roomId) return;
    const response = await fetch(`integrations/webex/connection`, {
      method: "POST",
      json: { roomId },
    });
    if (response?.ok) {
      setComplete(true);
      setError(undefined);
    }
  }, [roomId, fetch]);

  const handleTest = useCallback(async () => {
    setTestComplete(false);
    const response = await fetch(`integrations/webex/test`, {
      method: "POST",
    });
    if (response?.ok) {
      setTestComplete(true);
      setError(undefined);
    }
  }, [fetch]);

  const handleRemove = useCallback(async () => {
    await fetch(`integrations/webex/connection`, {
      method: "DELETE",
    });
    setComplete(false);
    setTestComplete(false);
    setError(undefined);
    setRoomId(undefined);
  }, [fetch]);

  const handleSetRoomId = useCallback(
    (value: string | undefined) => {
      setRoomId(value);
      if (value !== config.doc?.data?.roomId) {
        setComplete(false);
      }
      // Reset membership check when room changes
      setMembershipChecked(false);
      setIsBotMember(false);
    },
    [config.doc?.data?.roomId]
  );

  const handleCheckMembership = useCallback(async () => {
    if (!roomId) return;
    setCheckingMembership(true);
    try {
      const response = await fetch(
        `integrations/webex/rooms/${roomId}/membership`,
        {
          method: "GET",
        }
      );
      if (response?.ok) {
        const data = await response.json();
        setIsBotMember(data.isBotMember);
        setMembershipChecked(true);
        setError(undefined);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCheckingMembership(false);
    }
  }, [roomId, fetch]);

  const handleAddBot = useCallback(async () => {
    if (!roomId) return;
    setAddingBot(true);
    try {
      const response = await fetch(
        `integrations/webex/rooms/${roomId}/add-bot`,
        {
          method: "POST",
        }
      );
      if (response?.ok) {
        setIsBotMember(true);
        setError(undefined);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAddingBot(false);
    }
  }, [roomId, fetch]);

  const handleSwitchRoom = useCallback(() => {
    handleSetRoomId(undefined);
  }, [handleSetRoomId]);

  const handleRoomIdInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleSetRoomId(e.target.value);
    },
    [handleSetRoomId]
  );

  const filterRoomOption = useCallback(
    (input: string, option: { label: string; value: string } | undefined) =>
      (option?.label ?? "")
        .toString()
        .toLowerCase()
        .includes(input.toLowerCase()),
    []
  );

  return (
    <OAuth2IntegrationCard
      mode="secret"
      title="Webex"
      integration="webex"
      integrationDoc={config.doc}
      logo={<WebexLogo />}
      authExtra={authExtra}
      preamble={
        !isInstalled && !complete && !loading && !error ? (
          <Preamble />
        ) : undefined
      }
      onRemove={isInstalled ? handleRemove : undefined}
    >
      {error ? (
        <Alert type="error" message={error} />
      ) : complete && roomId ? (
        <>
          <Alert
            type="success"
            message="Webex is connected!"
            style={{ marginBottom: "1em" }}
          />
          <Typography.Text>
            <b>Room:</b> {selectedRoom?.title || roomId}
          </Typography.Text>
          <br />
          <br />
          <Button
            type="primary"
            onClick={handleTest}
            loading={fetching}
            style={{ marginRight: "0.5em" }}
          >
            Send Test Message
          </Button>
          <Button onClick={handleSwitchRoom} disabled={fetching}>
            Switch Room
          </Button>
          {testComplete && (
            <>
              <br />
              <br />
              <Alert
                type="success"
                message="Test message sent successfully! Check your Webex room."
              />
            </>
          )}
        </>
      ) : isInstalled ? (
        <>
          <Typography.Text>
            Select a Webex room to receive notifications:
          </Typography.Text>
          <br />
          <br />
          {rooms.length > 0 ? (
            <Select
              placeholder="Select a room"
              value={roomId}
              onChange={handleSetRoomId}
              disabled={fetching || loadingRooms}
              loading={loadingRooms}
              style={{ width: "100%", marginBottom: "1em" }}
              showSearch
              filterOption={filterRoomOption}
              options={rooms.map((room) => ({
                value: room.id,
                label: room.title,
              }))}
            />
          ) : (
            <>
              <Alert
                type="info"
                message="No rooms found"
                description="The bot must be added to a room first. Add the bot to a Webex room, then enter the Room ID below."
                style={{ marginBottom: "1em" }}
              />
              <Typography.Text>Enter Room ID:</Typography.Text>
              <br />
              <br />
              <Input
                placeholder="Room ID (e.g., Y2lzY29zcGFyazovL3VzL1JPT00vYmJjZWIxYWQtNDNmMS0zYjU4LTkxNDctZjE0YmIwYzRkMTU0)"
                value={roomId}
                onChange={handleRoomIdInputChange}
                disabled={fetching}
                style={{ marginBottom: "1em" }}
              />
            </>
          )}
          {roomId && (
            <>
              <Button
                onClick={handleCheckMembership}
                loading={checkingMembership}
                style={{ marginBottom: "1em", marginRight: "0.5em" }}
              >
                Check Bot Status
              </Button>
              {membershipChecked && (
                <>
                  {isBotMember ? (
                    <Alert
                      type="success"
                      message="Bot is in this room"
                      style={{ marginBottom: "1em" }}
                    />
                  ) : (
                    <Alert
                      type="warning"
                      message="Bot is not in this room"
                      description="Add the bot to send notifications to this room."
                      action={
                        <Button
                          size="small"
                          type="primary"
                          onClick={handleAddBot}
                          loading={addingBot}
                        >
                          Add Bot to Room
                        </Button>
                      }
                      style={{ marginBottom: "1em" }}
                    />
                  )}
                </>
              )}
            </>
          )}
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={fetching}
            disabled={!roomId}
          >
            Connect
          </Button>
        </>
      ) : null}
    </OAuth2IntegrationCard>
  );
};

export { WebexLogo };
